import { AppStore } from "@/store";
import { toastModule } from "@/modules/toast";
import { SessionContextMention } from "@/mds";
import { INoteObservable } from "@/store/note";
import { TrackedEvent, trackEvent } from "@/domains/metrics";

export const loadOrganizableNotes = async ({
  noteIds,
  store,
}: {
  noteIds: string[];
  store: AppStore;
}): Promise<INoteObservable[] | null> => {
  const notes: INoteObservable[] = [];

  for (const noteId of noteIds) {
    const note = await store.notes.getAsync(noteId);

    if (note) {
      notes.push(note);
    }
  }

  if (notes.length === 0) {
    toastModule.triggerToast({
      content: <div>Unable to find selected notes.</div>,
    });

    return null;
  }

  return notes;
};

export const autoOrganizeNotes = async ({
  noteIds,
  store,
}: {
  noteIds: string[];
  store: AppStore;
}): Promise<boolean> => {
  if (!store.floatingAgent.isMemAgentAccessEnabled) {
    return false;
  }

  const notes = await loadOrganizableNotes({ noteIds, store });

  if (!notes) {
    return false;
  }

  const mentions: SessionContextMention[] = notes.map(note => ({
    kind: "NOTE",
    note_id: note.id,
    note_primary_label: note.title,
  }));

  const requestContent =
    notes.length === 1 ? `Organize this note for me` : `Organize these notes for me`;

  const sent = await store.pageStore?.floatingAgentPage.sendUserMessage({
    content: requestContent,
    source: "text",
    context: { mentions },
  });

  // sendUserMessage returns false (no throw) when the quota isn't ready or is
  // exceeded — it opens the upsell instead. Only follow through on a real send.
  if (!sent) {
    return false;
  }

  store.floatingAgent.requestOpenPanel();

  trackEvent(TrackedEvent.AutoOrganizeNote, {
    requestContent,
    context: { mentions },
  });
  return true;
};
